import React, { useState, useEffect, useMemo } from 'react'
import './A1MenuButtons.css';

const DUMMY_COLORS = [
    '#2564eb6b',
    '#7c3aed6b',
    '#0890b26b',
    '#0596686b',
    '#d977066b',
    '#dc26266b',
    '#0285c76b',
    '#7e22ce6b',
    '#0d94896b',
    '#ec48996b',
    '#f43f5e6b',
    '#e11d486b',
    '#be123c6b',
    '#ea580c6b',
    '#f973166b',
    '#c2410c6b',
    '#b453096b',
    '#ca8a046b',
    '#eab3086b',
    '#a162076b',
    '#9333ea6b',
    '#8b5cf66b',
    '#6366f16b',
    '#4f46e56b',
    '#1d4ed86b',
    '#3b82f66b',
    '#2563eb6b',
    '#0f766e6b',
    '#06b6d46b',
    '#14b8a66b',
    '#a855f76b',
    '#db27776b',
    '#c026d36b',
    '#701a756b',
    '#7f1d1d6b',
    '#991b1b6b',
    '#b91c1c6b',
    '#9a34126b',
    '#92400e6b',
    '#78350f6b',
    '#5252526b',
    '#4040406b',
    '#1f29376b',
]

const getButtonColor = (buttonColor, index) =>
    DUMMY_COLORS[index % DUMMY_COLORS.length]

const getContrastColor = (hex) => {
    if (!hex) return '#fff'

    const c = hex.replace('#', '')

    const r = parseInt(c.substring(0, 2), 16)
    const g = parseInt(c.substring(2, 4), 16)
    const b = parseInt(c.substring(4, 6), 16)

    const luminance =
        (0.299 * r + 0.587 * g + 0.114 * b) / 255

    return luminance > 0.55 ? '#1a0033' : '#fff'
}

const A1MenuButtons = (props) => {

    const {
        data,
        addMessages,
        showMaximumChat,
        loading,
        scrollToBottom,
        viewMenuInfo,
        openWorkflow,
        tabs = []
    } = props

    const [clickedIndex, setClickedIndex] = useState(null)

    // Sidebar hide/show state
    const [isSidebarHidden, setIsSidebarHidden] = useState(false)

    const [expandedSections, setExpandedSections] = useState(() => {
        try {
            const saved = sessionStorage.getItem('expandedSections')
            return saved ? JSON.parse(saved) : {}
        } catch {
            return {}
        }
    })

    const item = data?.data
        ? JSON.parse(data.data)
        : null

    const mappingList = data?.mappingList || []
    const outOfTheGrouping = data?.outOfTheGrouping || {}
    const colorMap = data?.colorMap || {}
    const escalationQuickTabs =
        data?.staticEscalationQuickTabs || {}

    const disabledMappedIds = viewMenuInfo
        ? viewMenuInfo
            .split(',')
            .map(i => parseInt(i.trim()))
        : []

    const isButtonDisabled = (button) =>
        disabledMappedIds.includes(button.mappedId)

    const isButtonSelected = (button) =>
        tabs.some(t =>
            t.label === button.buttonName ||
            t.label === button.ctaName
        )

    const hasData = item && item.length > 0

    const groupedButtons = useMemo(() => {

        if (!item) return {}

        return item.reduce((acc, button) => {

            const category =
                button?.category || 'Others'

            if (!acc[category]) {
                acc[category] = []
            }

            acc[category].push(button)

            return acc

        }, {})

    }, [item])

    const orderedCategories = useMemo(() => {

        const categories =
            Object.keys(groupedButtons)

        const outOfGroupKeys =
            Object.keys(outOfTheGrouping)

        if (mappingList.length > 0) {

            return mappingList.filter(
                cat =>
                    categories.includes(cat) ||
                    outOfGroupKeys.includes(cat)
            )
        }

        return [
            ...categories,
            ...outOfGroupKeys.filter(
                k => !categories.includes(k)
            )
        ]

    }, [
        groupedButtons,
        mappingList,
        outOfTheGrouping
    ])

    useEffect(() => {

        if (
            orderedCategories.length > 0 &&
            Object.keys(expandedSections).length === 0
        ) {

            const initial = {}

            orderedCategories.forEach(cat => {
                initial[cat] = false
            })

            setExpandedSections(initial)
        }

    }, [orderedCategories])

    useEffect(() => {

        sessionStorage.setItem(
            'expandedSections',
            JSON.stringify(expandedSections)
        )

    }, [expandedSections])

    useEffect(() => {

        if (!loading) {
            setClickedIndex(null)
        }

    }, [loading])

    const outOfGroupingButtons = useMemo(() => {

        const entries =
            Object.entries(outOfTheGrouping)

        if (mappingList.length > 0) {

            return mappingList
                .filter(cat => outOfTheGrouping[cat])
                .map(cat => outOfTheGrouping[cat])
                .filter(
                    b => b && b.active !== false
                )
        }

        return entries
            .map(([, v]) => v)
            .filter(
                b => b && b.active !== false
            )

    }, [
        outOfTheGrouping,
        mappingList
    ])

    useEffect(() => {

        if (
            openWorkflow &&
            clickedIndex !== null
        ) {

            const timer = setTimeout(
                () => setClickedIndex(null),
                1000
            )

            return () => clearTimeout(timer)
        }

    }, [
        clickedIndex,
        openWorkflow
    ])

    if (!hasData || !showMaximumChat) {
        return null
    }

    const checkEndpoint = (button) => {

        if (
            button?.endpoint ===
            "storeCurrentOpNode"
        ) {
            return "MenuButton"
        }

        return null
    }

    const toggleSection = (category) => {

        setExpandedSections(prev => ({
            ...prev,
            [category]: !prev[category]
        }))
    }

    return (
        <>

            {/* =====================================================
                QUICK ACTIONS SIDEBAR
            ====================================================== */}

            <div
                className={`quick-actions-sidebar ${
                    isSidebarHidden
                        ? 'closed'
                        : ''
                }`}
            >

                {/* Sidebar Header */}

                <div className="sidebar-header">

                    <span className="sidebar-header-title">
                        Quick Actions
                    </span>

                    <button
                        type="button"
                        className="sidebar-hide-btn"
                        onClick={() =>
                            setIsSidebarHidden(true)
                        }
                        aria-label="Hide Quick Actions"
                        title="Hide Quick Actions"
                    >
                        <span className="sidebar-hide-arrow">
                            ›
                        </span>
                    </button>

                </div>


                {/* Sidebar Content */}

                <div className="sidebar-buttons">

                    {/* Static escalation lists */}

                    {Object.keys(
                        escalationQuickTabs
                    ).length > 0 && (

                        <div className="escalation-tabs-wrapper">
                            <div className="escalation-tabs-body">

                                    {Object.entries(
                                        escalationQuickTabs
                                    ).map(
                                        ([category, items]) => (

                                            <div
                                                key={`esc-${category}`}
                                                className="sidebar-section"
                                            >

                                                <div
                                                    className="sidebar-section-header"
                                                    onClick={() =>
                                                        toggleSection(
                                                            `esc-${category}`
                                                        )
                                                    }
                                                >

                                                    <span className="sidebar-section-title">
                                                        {category}
                                                    </span>

                                                    <span
                                                        className="sidebar-section-icon"
                                                        style={{
                                                            background:
                                                                'rgb(55 199 255)'
                                                        }}
                                                    >
                                                        {
                                                            expandedSections[
                                                                `esc-${category}`
                                                            ]
                                                                ? '−'
                                                                : '+'
                                                        }
                                                    </span>

                                                </div>


                                                {expandedSections[
                                                    `esc-${category}`
                                                ] && (

                                                    <div className="sidebar-section-buttons">

                                                        {items.map(
                                                            (
                                                                item,
                                                                index
                                                            ) => (

                                                                <button
                                                                    key={index}
                                                                    className={`sidebar-menu-button ${
                                                                        tabs.some(
                                                                            t =>
                                                                                t.label ===
                                                                                item.subCategory
                                                                        )
                                                                            ? 'sidebar-menu-button--selected'
                                                                            : ''
                                                                    }`}
                                                                    disabled={
                                                                        loading ||
                                                                        clickedIndex !==
                                                                            null ||
                                                                        tabs.some(
                                                                            t =>
                                                                                t.label ===
                                                                                    item.subCategory ||
                                                                                t.label ===
                                                                                    item.ctaName
                                                                        )
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            'rgb(131 220 255)'
                                                                    }}
                                                                    onClick={() => {

                                                                        setClickedIndex(
                                                                            `esc-${category}-${index}`
                                                                        )

                                                                        if (
                                                                            openWorkflow
                                                                        ) {

                                                                            openWorkflow(
                                                                                item.subCategory,
                                                                                item.node,
                                                                                item.id,
                                                                                item.id
                                                                            )

                                                                        } else {

                                                                            addMessages(
                                                                                `${item.subCategory}- QA`,
                                                                                item.node,
                                                                                item.subCategory,
                                                                                `${item.subCategory} - QA`
                                                                            )
                                                                        }

                                                                        scrollToBottom?.()
                                                                    }}
                                                                >

                                                                    <span className="sidebar-button-text">
                                                                        {
                                                                            item.subCategory
                                                                        }
                                                                    </span>

                                                                </button>

                                                            )
                                                        )}

                                                    </div>

                                                )}

                                            </div>

                                        )
                                    )}

                            </div>

                        </div>

                    )}


                    {/* =================================================
                        MAIN CATEGORIES
                    ================================================== */}

                    {orderedCategories.length > 0 && (

                        <>

                            {orderedCategories.map(
                                category => {

                                    /* OUT OF GROUP */

                                    if (
                                        outOfTheGrouping[
                                            category
                                        ]
                                    ) {

                                        const button =
                                            outOfTheGrouping[
                                                category
                                            ]

                                        if (
                                            button.active === false
                                        ) {
                                            return null
                                        }

                                        return (
                                            <div
                                                key={category}
                                                className="sidebar-section-buttons sidebar-out-of-group"
                                            >

                                                <button
                                                    onClick={() => {

                                                        setClickedIndex(
                                                            `oog-${category}`
                                                        )

                                                        if (
                                                            openWorkflow
                                                        ) {

                                                            openWorkflow(
                                                                button?.buttonName,
                                                                button?.mappedButtonOpCode ||
                                                                    button?.buttonOpCode,
                                                                button?.mappedId
                                                            )

                                                        } else {

                                                            addMessages(
                                                                `${button?.buttonName}- QA`,
                                                                button?.buttonOpCode,
                                                                button?.buttonName,
                                                                `${button?.buttonName} - QA`,
                                                                null,
                                                                checkEndpoint(
                                                                    button
                                                                )
                                                            )
                                                        }

                                                        scrollToBottom?.()
                                                    }}

                                                    className={`sidebar-menu-button sidebar-menu-button--oog ${
                                                        isButtonSelected(
                                                            button
                                                        )
                                                            ? 'sidebar-menu-button--selected'
                                                            : ''
                                                    }`}

                                                    disabled={
                                                        loading ||
                                                        clickedIndex !==
                                                            null ||
                                                        isButtonDisabled(
                                                            button
                                                        ) ||
                                                        isButtonSelected(
                                                            button
                                                        )
                                                    }

                                                    style={{
                                                        backgroundColor:
                                                            button.buttonColor,

                                                        color:
                                                            getContrastColor(
                                                                getButtonColor(
                                                                    button.buttonColor,
                                                                    0
                                                                )
                                                            )
                                                    }}
                                                >

                                                    <span className="sidebar-button-text">
                                                        {
                                                            button?.buttonName
                                                        }
                                                    </span>

                                                </button>

                                            </div>
                                        )
                                    }


                                    /* NORMAL CATEGORY */

                                    if (
                                        !groupedButtons[
                                            category
                                        ]
                                    ) {
                                        return null
                                    }

                                    return (
                                        <div
                                            key={category}
                                            className="sidebar-section"
                                            style={{
                                                borderColor:
                                                    groupedButtons[
                                                        category
                                                    ][0]
                                                        ?.buttonColor,

                                                backgroundColor:
                                                    colorMap[
                                                        category
                                                    ] ||
                                                    undefined
                                            }}
                                        >

                                            <div
                                                className="sidebar-section-header"
                                                onClick={() =>
                                                    toggleSection(
                                                        category
                                                    )
                                                }
                                            >

                                                <span className="sidebar-section-title">
                                                    {category}
                                                </span>

                                                <span
                                                    className="sidebar-section-icon"
                                                    style={{
                                                        background:
                                                            groupedButtons[
                                                                category
                                                            ][0]
                                                                ?.buttonColor
                                                    }}
                                                >
                                                    {
                                                        expandedSections[
                                                            category
                                                        ]
                                                            ? '−'
                                                            : '+'
                                                    }
                                                </span>

                                            </div>


                                            {expandedSections[
                                                category
                                            ] && (

                                                <div className="sidebar-section-buttons">

                                                    {groupedButtons[
                                                        category
                                                    ].map(
                                                        (
                                                            button,
                                                            index
                                                        ) => (

                                                            <button
                                                                key={index}

                                                                onClick={() => {

                                                                    setClickedIndex(
                                                                        `${category}-${index}`
                                                                    )

                                                                    if (
                                                                        openWorkflow
                                                                    ) {

                                                                        openWorkflow(
                                                                            button?.buttonName,
                                                                            button?.mappedButtonOpCode ||
                                                                                button?.buttonOpCode,
                                                                            button?.mappedId ||
                                                                                index
                                                                        )

                                                                    } else {

                                                                        addMessages(
                                                                            `${button?.buttonName}- QA`,
                                                                            button?.buttonOpCode,
                                                                            button?.buttonName,
                                                                            `${button?.buttonName} - QA`,
                                                                            null,
                                                                            checkEndpoint(
                                                                                button
                                                                            )
                                                                        )
                                                                    }

                                                                    scrollToBottom?.()
                                                                }}

                                                                className={`sidebar-menu-button ${
                                                                    isButtonSelected(
                                                                        button
                                                                    )
                                                                        ? 'sidebar-menu-button--selected'
                                                                        : ''
                                                                }`}

                                                                disabled={
                                                                    loading ||
                                                                    clickedIndex !==
                                                                        null ||
                                                                    isButtonDisabled(
                                                                        button
                                                                    ) ||
                                                                    isButtonSelected(
                                                                        button
                                                                    )
                                                                }

                                                                style={{
                                                                    backgroundColor:
                                                                        button.buttonColor,

                                                                    color:
                                                                        getContrastColor(
                                                                            getButtonColor(
                                                                                button.buttonColor,
                                                                                index
                                                                            )
                                                                        )
                                                                }}
                                                            >

                                                                {/* Button Image */}

                                                                {(() => {

                                                                    try {

                                                                        const imgData =
                                                                            button?.buttonImageURL
                                                                                ? JSON.parse(
                                                                                    button.buttonImageURL
                                                                                )
                                                                                : null

                                                                        if (
                                                                            imgData?.img
                                                                        ) {

                                                                            return (
                                                                                <img
                                                                                    src={
                                                                                        imgData.img
                                                                                    }
                                                                                    alt=""
                                                                                    className="sidebar-button-img"
                                                                                />
                                                                            )
                                                                        }

                                                                    } catch {}

                                                                    return null

                                                                })()}


                                                                {/* Button Name */}

                                                                <span className="sidebar-button-text">
                                                                    {
                                                                        button?.buttonName
                                                                    }
                                                                </span>


                                                                {/* Button Badge */}

                                                                {(() => {

                                                                    try {

                                                                        const imgData =
                                                                            button?.buttonImageURL
                                                                                ? JSON.parse(
                                                                                    button.buttonImageURL
                                                                                )
                                                                                : null

                                                                        if (
                                                                            imgData?.button
                                                                        ) {

                                                                            return (
                                                                                <span className="sidebar-button-badge">
                                                                                    {
                                                                                        imgData.button
                                                                                    }
                                                                                </span>
                                                                            )
                                                                        }

                                                                    } catch {}

                                                                    return null

                                                                })()}

                                                            </button>

                                                        )
                                                    )}

                                                </div>

                                            )}

                                        </div>
                                    )
                                }
                            )}

                        </>
                    )}

                </div>

            </div>


            {/* =====================================================
                SHOW SIDEBAR BUTTON
            ====================================================== */}

            {isSidebarHidden && (

                <button
                    type="button"
                    className="sidebar-show-btn"
                    onClick={() =>
                        setIsSidebarHidden(false)
                    }
                    aria-label="Show Quick Actions"
                    title="Show Quick Actions"
                >

                    <span className="sidebar-show-arrow">
                        ‹
                    </span>

                    <span className="sidebar-show-label">
                        Quick Actions
                    </span>

                </button>

            )}

        </>
    )
}

export default A1MenuButtons
